import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Helmet } from "react-helmet-async";
import { FaPhone } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

function ContactUs() {
  const { t } = useTranslation("contactus");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  // Handling input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (phone) => {
    setFormData({ ...formData, phone });
  };

  // Handling form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, phone, subject, message } = formData;

    if (!name || !email || !phone || !subject || !message) {
      toast.error(t("errors.fillAllFields"));
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/contactus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(t("success.messageSent"));
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        toast.error(`${t("errors.failedToSend")}: ${result.error}`);
      }
    } catch (error) {
      toast.error(t("errors.somethingWentWrong"));
    }
  };

  return (
    <>
      <Helmet>
        <title>{t("helmet.contactUsTitle")}</title>
        <meta name="description" content={t("helmet.contactUsDescription")} />
      </Helmet>

      <div className="container mx-auto p-8">
        <ToastContainer position="top-center" autoClose={3000} />

        <div className="flex flex-col md:flex-row justify-between">
          {/* Contact Form Section */}
          <div className="w-full md:w-1/2 bg-gray-100 text-black p-8 rounded-lg mb-8 md:mb-0 md:mr-4 shadow-md">
            <h2 className="text-3xl font-semibold mb-6">{t("contactUsTitle")}</h2>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="flex flex-col md:flex-row md:space-x-4">
                <div className="w-full mb-4 md:mb-0">
                  <label htmlFor="name" className="block mb-1 font-semibold">
                    {t("nameLabel")}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full p-2 rounded-md text-gray-800"
                    placeholder={t("namePlaceholder")}
                  />
                </div>
                <div className="w-full">
                  <label htmlFor="email" className="block mb-1 font-semibold">
                    {t("emailLabel")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full p-2 rounded-md text-gray-800"
                    placeholder={t("emailPlaceholder")}
                  />
                </div>
              </div>

              <div className="col-span-2 flex items-center gap-2">
                <FaPhone className="text-gray-600" />
                <label className="block text-gray-700 font-semibold mb-1">{t("mobileNumberLabel")}</label>
              </div>
              <div className="col-span-2 md:col-span-1">
                <PhoneInput
                  placeholder={t("phonePlaceholder")}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  defaultCountry="US"
                  className="w-full p-2 border rounded"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block mb-1 font-semibold">
                  {t("subjectLabel")}
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full p-2 rounded-md text-gray-800"
                  placeholder={t("subjectPlaceholder")}
                />
              </div>
              <div>
                <label htmlFor="message" className="block mb-1 font-semibold">
                  {t("messageLabel")}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full p-2 rounded-md text-gray-800"
                  placeholder={t("messagePlaceholder")}
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full p-3 bg-gray-700 hover:bg-gray-600 rounded-md font-semibold text-white mt-4"
              >
                {t("sendMessageButton")}
              </button>
            </form>
          </div>

          {/* Image-based Section */}
          <div className="w-full md:w-1/2 text-black p-8 rounded-lg md:ml-4 mb-12">
            <h2 className="text-3xl font-semibold mb-6">{t("getInTouchTitle")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <img src="/assets/contactus.jpg" alt={t("images.office")} className="rounded-md" />
              <img src="/assets/Surat-Diamond.jpg" alt={t("images.interior")} className="rounded-md" />
            </div>
            <div className="mt-6">
              <h3 className="text-xl font-semibold">{t("headOffice")}</h3>
              <p>{t("officeAddress")}</p>
              <a href="mailto:info@wellstarjewels.com" className="text-blue-600">{t("emailLink")}</a>
              <br />
              <a href="tel:+919687247163" className="text-blue-600">{t("phoneLink")}</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ContactUs;
